/**
 * Simple 5-action state machine for beat navigation within a single thread.
 *
 * State shape: { currentIndex, status, totalBeats }
 * Status transitions: IDLE -> PLAYING -> COMPLETE (via NEXT at last beat).
 * SET_BEATS reinitializes the machine when a new thread loads.
 * @module playerReducer
 */

import { PLAYER_STATUS } from './constants'

/** @enum {string} Action types for the player reducer */
export const ACTIONS = {
  NEXT: 'NEXT',
  PREV: 'PREV',
  GO_TO: 'GO_TO',
  RESET: 'RESET',
  SET_BEATS: 'SET_BEATS',
}

export const initialState = {
  currentIndex: 0,
  status: PLAYER_STATUS.IDLE,
  totalBeats: 0,
}

/**
 * Reducer for beat navigation. Handles 5 action types:
 * - SET_BEATS: Initialize with a beat count (resets index to 0)
 * - NEXT: Advance one beat, or transition to COMPLETE at the end
 * - PREV: Go back one beat (clamped to 0)
 * - GO_TO: Jump to a specific index (clamped to valid range)
 * - RESET: Return to beat 0 without changing totalBeats
 *
 * @param {{ currentIndex: number, status: string, totalBeats: number }} state
 * @param {{ type: string, payload?: number }} action
 * @returns {{ currentIndex: number, status: string, totalBeats: number }}
 */
export function playerReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_BEATS:
      return {
        currentIndex: 0,
        status: action.payload > 0 ? PLAYER_STATUS.PLAYING : PLAYER_STATUS.IDLE,
        totalBeats: action.payload,
      }

    case ACTIONS.NEXT: {
      if (state.currentIndex >= state.totalBeats - 1) {
        return { ...state, status: PLAYER_STATUS.COMPLETE }
      }
      return { ...state, currentIndex: state.currentIndex + 1, status: PLAYER_STATUS.PLAYING }
    }

    case ACTIONS.PREV:
      return {
        ...state,
        currentIndex: Math.max(0, state.currentIndex - 1),
        status: PLAYER_STATUS.PLAYING,
      }

    case ACTIONS.GO_TO:
      return {
        ...state,
        currentIndex: Math.max(0, Math.min(action.payload, state.totalBeats - 1)),
        status: PLAYER_STATUS.PLAYING,
      }

    case ACTIONS.RESET:
      return {
        ...state,
        currentIndex: 0,
        status: state.totalBeats > 0 ? PLAYER_STATUS.PLAYING : PLAYER_STATUS.IDLE,
      }

    default:
      return state
  }
}
